import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Moon, Sun, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bookingAPI, greeterAPI } from '../../services/api';
import Loader from '../common/Loader';
import UserBookingForm from '../user/UserBookingForm';
import BookingDetailCard from '../common/BookingDetailCard';

// Icons
const BookingsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const ViewIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);


const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [greeterInfoModal, setGreeterInfoModal] = useState({ isOpen: false, greeter: null });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('userDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const bookingsResponse = await bookingAPI.getUserBookings({ limit: 5 });

      if (bookingsResponse.success) {
        setBookings(bookingsResponse.data.bookings);
      }
    } catch (error) {
      setError('Failed to fetch user data');
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      // Navigate to login page after successful logout
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleNewBooking = () => {
    setShowBookingForm(true);
  };

  const handleBookingSuccess = async () => {
    // Add a small delay to ensure the booking is processed on the server
    setTimeout(async () => {
      await fetchUserData();
    }, 1000);
  };

  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
  };

  const handleViewAllBookings = () => {
    navigate('/user/bookings');
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetail(true);
  };

  const handleCloseBookingDetail = () => {
    setShowBookingDetail(false);
    setSelectedBooking(null);
  };

  const handleAssignmentClick = async (booking) => {
    if (booking.isAssigned && booking.assignedGreeter) {
      try {
        const response = await greeterAPI.getGreeterInfoForBooking(booking._id);
        if (response.success) {
          setGreeterInfoModal({ isOpen: true, greeter: response.data.greeter });
        } else {
          toast.error('Failed to fetch greeter information');
        }
      } catch (error) {
        console.error('Error fetching greeter info:', error);
        toast.error('Failed to fetch greeter information');
      }
    }
  };

  const handleRefreshBookings = async () => {
    try {
      setLoading(true);
      const bookingsResponse = await bookingAPI.getUserBookings({ limit: 5 });
      
      if (bookingsResponse.success) {
        setBookings(bookingsResponse.data.bookings);
      }
    } catch (error) {
      setError('Failed to refresh bookings');
      console.error('Error refreshing bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader isDarkMode={isDarkMode} />;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode
        ? 'bg-gradient-to-br from-skyone-page-dark via-[#15101f] to-skyone-page-dark'
        : 'bg-gradient-to-br from-skyone-page via-white to-primary-50/50'
    }`}>
      {/* Header */}
      <div className={`shadow-lg border-b transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-skyone-card-dark border-skyone-border-dark' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-primary-600 to-primary-800 ring-2 ring-accent-500/25'
                    : 'bg-gradient-to-br from-primary-600 to-primary-800'
                }`}>
                  <span className="text-white text-xl font-bold">
                    {user?.firstName?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div>
                <h1 className={`text-3xl font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Welcome back, {user?.firstName}!
                </h1>
                <p className={`mt-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                }`}>Manage your airport meet & greet bookings</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{user?.firstName} {user?.lastName}</p>
                <p className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                }`}>{user?.email}</p>
              </div>
              
              {/* Profile Button */}
              <button
                onClick={() => navigate('/user/profile')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  isDarkMode
                    ? 'bg-primary-600 text-white hover:bg-primary-500 shadow-lg shadow-primary-900/40'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                Profile
              </button>
              
              {/* Dark Mode Toggle */}
              <button
                onClick={() => {
                  const newMode = !isDarkMode;
                  setIsDarkMode(newMode);
                  localStorage.setItem('userDarkMode', JSON.stringify(newMode));
                }}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark border border-skyone-border-dark' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
                }`}
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="w-4 h-4 mr-2" />
                    Light
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 mr-2" />
                    Dark
                  </>
                )}
              </button>
              
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                  isDarkMode 
                    ? 'bg-red-700 hover:bg-red-600 text-white border border-red-600' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {isLoggingOut ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Logging out...
                  </>
                ) : (
                  'Logout'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className={`mb-6 border rounded-md p-4 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-red-900/20 border-red-700' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className={`text-sm transition-colors duration-300 ${
              isDarkMode ? 'text-red-300' : 'text-red-700'
            }`}>{error}</div>
          </div>
        )}


        {/* Quick Actions */}
        <div className={`shadow-lg rounded-xl border mb-8 transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-skyone-card-dark border-skyone-border-dark' 
            : 'bg-white/90 border-gray-100 backdrop-blur-sm'
        }`}>
          <div className="px-6 py-6">
            <h3 className={`text-xl font-bold mb-6 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button 
                onClick={handleNewBooking}
                className={`group text-white px-8 py-4 rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center space-x-2 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 shadow-primary-900/30'
                    : 'bg-gradient-to-r from-primary-600 to-primary-800 hover:from-primary-700 hover:to-primary-900'
                }`}
              >
                <PlusIcon />
                <span>New Booking Request</span>
              </button>
              <button 
                onClick={handleViewAllBookings}
                className={`group px-8 py-4 rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center space-x-2 ${
                  isDarkMode
                    ? 'bg-skyone-inset-dark text-white border border-skyone-border-dark hover:bg-skyone-border-dark hover:border-accent-500/40'
                    : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white'
                }`}
              >
                <ViewIcon />
                <span>View All Bookings</span>
              </button>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className={`shadow-lg rounded-xl border transition-colors duration-300 ${
          isDarkMode
            ? 'bg-skyone-card-dark border-skyone-border-dark'
            : 'bg-white/90 border-gray-100 backdrop-blur-sm'
        }`}>
          <div className="px-6 py-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Recent Bookings
              </h3>
              <button
                onClick={handleRefreshBookings}
                disabled={loading}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'text-skyone-muted-dark hover:text-white hover:bg-skyone-inset-dark'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
                title="Refresh bookings"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
            {bookings.length > 0 ? (
              <div className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y transition-colors duration-300 ${
                    isDarkMode ? 'divide-skyone-border-dark' : 'divide-gray-200'
                  }`}>
                    <thead className={`transition-colors duration-300 ${
                      isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                    }`}>
                      <tr>
                        <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          Service
                        </th>
                        <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          Amount
                        </th>
                        <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          Status
                        </th>
                        <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          Date
                        </th>
                        <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          Assignment
                        </th>
                        <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y transition-colors duration-300 ${
                      isDarkMode 
                        ? 'bg-skyone-card-dark divide-skyone-border-dark' 
                        : 'bg-white divide-gray-200'
                    }`}>
                      {bookings.map((booking) => (
                        <tr key={booking._id} className={`transition-colors duration-150 ${
                          isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                        }`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-semibold transition-colors duration-300 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {booking.serviceDetails?.levelName 
                                ? `${booking.serviceDetails.categoryName || booking.serviceType} - ${booking.serviceDetails.levelName}`
                                : booking.serviceType
                              }
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-semibold transition-colors duration-300 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              £{booking.amount}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border transition-colors duration-300 ${
                              booking.status === 'paid' 
                                ? isDarkMode 
                                  ? 'bg-green-900 text-green-200 border-green-700'
                                  : 'bg-green-100 text-green-800 border-green-200'
                                : booking.status === 'pending'
                                ? isDarkMode
                                  ? 'bg-yellow-900 text-yellow-200 border-yellow-700'
                                  : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                : booking.status === 'failed'
                                ? isDarkMode
                                  ? 'bg-red-900 text-red-200 border-red-700'
                                  : 'bg-red-100 text-red-800 border-red-200'
                                : isDarkMode
                                  ? 'bg-skyone-inset-dark text-zinc-200 border-skyone-border-dark'
                                  : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                            }`}>
                              {new Date(booking.createdAt).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {booking.isAssigned ? (
                              <button
                                onClick={() => handleAssignmentClick(booking)}
                                className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-lg border transition-all duration-200 ${
                                  isDarkMode 
                                    ? 'text-green-200 bg-green-900 border-green-700 hover:bg-green-800 hover:text-green-100' 
                                    : 'text-green-700 bg-green-100 border-green-300 hover:bg-green-200 hover:text-green-800'
                                }`}
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                Assigned
                              </button>
                            ) : (
                              <span className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                              }`}>Not Assigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-3">
                              <button 
                                onClick={() => handleViewBooking(booking)}
                                className={`font-semibold transition-colors duration-150 flex items-center space-x-1 ${
                                  isDarkMode
                                    ? 'text-accent-400 hover:text-accent-300'
                                    : 'text-primary-600 hover:text-primary-800'
                                }`}
                              >
                                <ViewIcon />
                                <span>View</span>
                              </button>
                              {booking.status === 'pending' && (
                                <a 
                                  href={booking.paymentLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`font-semibold transition-colors duration-150 flex items-center space-x-1 ${
                                    isDarkMode 
                                      ? 'text-green-400 hover:text-green-300' 
                                      : 'text-green-600 hover:text-green-800'
                                  }`}
                                >
                                  <CreditCardIcon />
                                  <span>Pay</span>
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-inset-dark text-accent-400/90' : 'bg-gray-100 text-primary-600'
                }`}>
                  <BookingsIcon />
                </div>
                <p className={`text-lg font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-zinc-200' : 'text-gray-500'
                }`}>No bookings yet</p>
                <p className={`text-sm mt-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-400'
                }`}>Create your first booking to get started!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <UserBookingForm
          onClose={handleCloseBookingForm}
          onSuccess={handleBookingSuccess}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Booking Detail Modal */}
      <BookingDetailCard
        booking={selectedBooking}
        isOpen={showBookingDetail}
        onClose={handleCloseBookingDetail}
        isDarkMode={isDarkMode}
      />

      {/* Greeter Info Modal */}
      {greeterInfoModal.isOpen && (
        <div className="fixed inset-0 bg-skyone-page-dark/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark shadow-black/40' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Assigned Greeter
              </h3>
              <button
                onClick={() => setGreeterInfoModal({ isOpen: false, greeter: null })}
                className={`transition-colors duration-200 ${
                  isDarkMode ? 'text-skyone-muted-dark hover:text-white' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {greeterInfoModal.greeter && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isDarkMode ? 'bg-green-800' : 'bg-green-100'
                    }`}>
                      <svg className={`w-6 h-6 transition-colors duration-300 ${
                        isDarkMode ? 'text-green-300' : 'text-green-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h4 className={`text-lg font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {greeterInfoModal.greeter.firstName} {greeterInfoModal.greeter.lastName}
                    </h4>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                    }`}>{greeterInfoModal.greeter.email}</p>
                  </div>
                </div>
                
                <div className={`border-t pt-4 transition-colors duration-300 ${
                  isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
                }`}>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-zinc-300' : 'text-gray-700'
                      }`}>Phone:</span>
                      <p className={`transition-colors duration-300 ${
                        isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                      }`}>{greeterInfoModal.greeter.phoneNumber}</p>
                    </div>
                    <div>
                      <span className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-zinc-300' : 'text-gray-700'
                      }`}>Status:</span>
                      <p className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                        greeterInfoModal.greeter.isActive 
                          ? isDarkMode
                            ? 'bg-green-800 text-green-200' 
                            : 'bg-green-100 text-green-800'
                          : isDarkMode
                            ? 'bg-red-800 text-red-200'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {greeterInfoModal.greeter.isActive ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setGreeterInfoModal({ isOpen: false, greeter: null })}
                className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                  isDarkMode
                    ? 'bg-skyone-inset-dark text-zinc-200 border border-skyone-border-dark hover:bg-skyone-border-dark'
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
